import { useEffect, useState } from 'react';

import { ExpandMore, History } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

import { IMaintenance } from '@/interfaces/maintenance/IMaintenance';
import maintenancesService from '@/services/maintenances';

import MaintenanceDetails from '../MaintenanceDetails';

interface Props {
  vehicleId: string;
  /**
   * Changing this refetches the list. The parent passes the maintenance's
   * updatedAt so that starting or finishing it -- which can set dates far in
   * the past -- does not leave a stale, contradictory entry in this history.
   */
  refreshKey?: string;
}

export default function VehicleMaintenanceHistory({
  vehicleId,
  refreshKey,
}: Props) {
  const [loading, setLoading] = useState<boolean>(false);

  const [maintenanceList, setMaintenanceList] = useState<IMaintenance[]>([]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const maintenanceListResponse =
          await maintenancesService.listVehicleMaintenances(vehicleId);
        setMaintenanceList(maintenanceListResponse);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [vehicleId, refreshKey]);

  return (
    <Accordion sx={{ marginBottom: 2 }}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
      >
        <Stack gap={1} direction="row" alignItems="center">
          <History />
          <Typography variant="h6">
            {`Maintenances History (${maintenanceList.length})`}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ maxHeight: 500, overflow: 'auto' }}>
        {loading ? (
          <CircularProgress size={18} color="primary" />
        ) : (
          maintenanceList.map(maintenance => (
            <MaintenanceDetails
              key={maintenance.id}
              maintenance={maintenance}
              hideVehicleDetails
              showDivider
            />
          ))
        )}
      </AccordionDetails>
    </Accordion>
  );
}
